import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import Navbar from "../component/Header.js/Navbar/Navbar";
import Footer from "../component/Footer/Footer";
import { colors } from "../data";
import CompLoader from "../component/Loaders/CompLoader/CompLoader";
import {
  LocalShipping,
  Search,
  CheckCircle,
  RadioButtonChecked,
  LocationOn,
  Schedule,
  Inventory2,
  ErrorOutline,
} from "@mui/icons-material";

const Container = styled.div`
  min-height: 75vh;
  background-color: #fdfbf7;
  padding: 4rem 2rem 6rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentCard = styled.div`
  width: 100%;
  max-width: 800px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid #f0eae1;
  overflow: hidden;
  padding: 3.5rem 3rem;

  @media (max-width: 768px) {
    padding: 2.5rem 1.5rem;
  }
`;

const TitleSection = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;

  h1 {
    font-size: 2.8rem;
    color: #222;
    margin-bottom: 0.8rem;
    font-weight: 600;
  }

  p {
    font-size: 1.4rem;
    color: #666;
    max-width: 500px;
    margin: 0 auto;
    line-height: 1.5;
  }
`;

const SearchForm = styled.form`
  display: flex;
  gap: 1rem;
  width: 100%;
  max-width: 600px;
  margin: 0 auto 3rem;

  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;

  svg {
    position: absolute;
    left: 14px;
    color: #888;
    font-size: 2.2rem;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 16px 0 46px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 1.4rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: ${colors.mainColor};
    box-shadow: 0 0 0 3px rgba(81, 29, 0, 0.15);
  }
`;

const TrackButton = styled.button`
  height: 48px;
  padding: 0 28px;
  background-color: ${colors.mainColor};
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  transition: all 0.2s;

  &:hover {
    filter: brightness(1.08);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StatusOverview = styled.div`
  background: #fbf7f4;
  border: 1px solid #ebdcd5;
  border-radius: 10px;
  padding: 2rem;
  margin-bottom: 2.5rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.5rem;

  .stat-block {
    display: flex;
    flex-direction: column;
    gap: 0.4rem;

    .label {
      font-size: 1.2rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #777;
    }

    .val {
      font-size: 1.5rem;
      font-weight: 600;
      color: #222;
    }

    .badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 20px;
      font-size: 1.2rem;
      font-weight: 600;
      width: fit-content;
      background-color: #e8f5e9;
      color: #2e7d32;
    }
  }
`;

const TimelineSection = styled.div`
  margin-top: 2rem;

  h3 {
    font-size: 1.8rem;
    margin-bottom: 1.8rem;
    color: #333;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }
`;

const TimelineList = styled.div`
  position: relative;
  padding-left: 2.5rem;

  &::before {
    content: "";
    position: absolute;
    left: 11px;
    top: 10px;
    bottom: 10px;
    width: 2px;
    background: #e0d8d0;
  }
`;

const TimelineItem = styled.div`
  position: relative;
  margin-bottom: 2rem;

  &:last-child {
    margin-bottom: 0;
  }

  .bullet {
    position: absolute;
    left: -2.5rem;
    top: 2px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    svg {
      font-size: 1.8rem;
      color: ${(props) => (props.isLatest ? colors.mainColor : "#888")};
    }
  }

  .item-content {
    background: #ffffff;
    border: 1px solid #f0ede8;
    border-radius: 8px;
    padding: 1.2rem 1.6rem;

    .activity {
      font-size: 1.4rem;
      font-weight: 600;
      color: #222;
      margin-bottom: 0.3rem;
    }

    .meta {
      display: flex;
      flex-wrap: wrap;
      gap: 1.2rem;
      font-size: 1.2rem;
      color: #666;

      span {
        display: flex;
        align-items: center;
        gap: 0.4rem;
      }
    }
  }
`;

const ErrorBox = styled.div`
  background: #fff5f5;
  border: 1px solid #fed7d7;
  color: #c53030;
  padding: 1.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.4rem;
  margin-bottom: 2rem;

  svg {
    font-size: 2.2rem;
  }
`;

const TrackOrder = () => {
  const location = useLocation();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [trackingData, setTrackingData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const executeTrack = async (awbToTrack) => {
    if (!awbToTrack || awbToTrack.trim() === "") {
      setErrorMsg("Please enter a tracking or order number.");
      return;
    }

    setIsLoading(true);
    setErrorMsg("");
    setTrackingData(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/shipping/track-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ awb: awbToTrack.trim() }),
        }
      );

      const data = await res.json();

      if (res.ok && data.success) {
        setTrackingData(data);
      } else {
        setErrorMsg(
          data.message ||
            "Unable to find tracking details. Please verify your AWB or order number."
        );
      }
    } catch (err) {
      console.error("Tracking request error:", err);
      setErrorMsg("Network error occurred. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const awbFromUrl = params.get("awb") || params.get("tracking");
    if (awbFromUrl) {
      setTrackingNumber(awbFromUrl);
      executeTrack(awbFromUrl);
    }
  }, [location.search]);

  const handleSubmit = (e) => {
    e.preventDefault();
    executeTrack(trackingNumber);
  };

  return (
    <>
      <Navbar />
      <Container>
        <ContentCard>
          <TitleSection>
            <h1>Track Your Order</h1>
            <p>
              Enter your Delhivery AWB number or Order ID below to get real-time status updates on your delivery.
            </p>
          </TitleSection>

          <SearchForm onSubmit={handleSubmit}>
            <SearchInputWrapper>
              <Search />
              <SearchInput
                type="text"
                placeholder="Enter AWB or Order Number (e.g. 1234567890)"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
              />
            </SearchInputWrapper>
            <TrackButton type="submit" disabled={isLoading}>
              <LocalShipping style={{ fontSize: "1.8rem" }} />
              {isLoading ? "Checking..." : "Track Now"}
            </TrackButton>
          </SearchForm>

          {isLoading && <CompLoader />}

          {errorMsg && (
            <ErrorBox>
              <ErrorOutline />
              <span>{errorMsg}</span>
            </ErrorBox>
          )}

          {trackingData && (
            <>
              <StatusOverview>
                <div className="stat-block">
                  <span className="label">Status</span>
                  <span className="badge">{trackingData.status || "In Transit"}</span>
                </div>
                <div className="stat-block">
                  <span className="label">AWB / Waybill</span>
                  <span className="val">{trackingData.awb}</span>
                </div>
                {trackingData.destination && (
                  <div className="stat-block">
                    <span className="label">Destination</span>
                    <span className="val">{trackingData.destination}</span>
                  </div>
                )}
                {trackingData.expectedDelivery && (
                  <div className="stat-block">
                    <span className="label">Estimated Delivery</span>
                    <span className="val">{trackingData.expectedDelivery}</span>
                  </div>
                )}
              </StatusOverview>

              {trackingData.scans && trackingData.scans.length > 0 && (
                <TimelineSection>
                  <h3>
                    <Schedule /> Tracking Activity
                  </h3>
                  <TimelineList>
                    {trackingData.scans.map((scan, idx) => (
                      <TimelineItem key={idx} isLatest={idx === 0}>
                        <div className="bullet">
                          {idx === 0 ? <RadioButtonChecked /> : <CheckCircle />}
                        </div>
                        <div className="item-content">
                          <div className="activity">
                            {scan.activity || scan.status || "Package Update"}
                          </div>
                          <div className="meta">
                            {scan.dateTime && (
                              <span>
                                <Schedule style={{ fontSize: "1.4rem" }} />
                                {scan.dateTime}
                              </span>
                            )}
                            {scan.location && (
                              <span>
                                <LocationOn style={{ fontSize: "1.4rem" }} />
                                {scan.location}
                              </span>
                            )}
                          </div>
                        </div>
                      </TimelineItem>
                    ))}
                  </TimelineList>
                </TimelineSection>
              )}
            </>
          )}
        </ContentCard>
      </Container>
      <Footer />
    </>
  );
};

export default TrackOrder;
